import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Grid,
  CircularProgress,
  alpha,
  useTheme,
  Switch,
  FormControlLabel,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { QK } from "../../../../../../base/qk";
import { getInfectionById, updateInfection } from "../../../../../../api/services/observations";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";

const EditInfectionModal = ({ show, handleClose, selectedInfection }) => {
  const theme = useTheme();
  const { id: clientId } = useParams();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    start_date: "",
    date_time: "",
    infection_type: "",
    cleared: false,
    hospitalized: false,
    isolation_required: false,
    notes: "",
    handover: false,
    manager: false,
  });

  const [errors, setErrors] = useState({});

  const infectionTypes = [
    { value: "COVID-19", label: "COVID-19" },
    { value: "C.diff", label: "C.diff" },
    { value: "E.coli", label: "E.coli" },
    { value: "MRSA", label: "MRSA" },
    { value: "Norovirus", label: "Norovirus" },
    { value: "UTI", label: "UTI" },
    { value: "Other", label: "Other" },
  ];

  const { data: infection, isLoading } = useQuery(
    [QK.infection, selectedInfection],
    () => getInfectionById(selectedInfection),
    {
      enabled: !!selectedInfection && show,
      onSuccess: (data) => {
        if (data) {
          setFormData({
            start_date: data.start_date || "",
            date_time: data.date_time ? new Date(data.date_time).toTimeString().slice(0, 5) : "",
            infection_type: data.infection_type || "",
            cleared: data.cleared || false,
            hospitalized: data.hospitalized || false,
            isolation_required: data.isolation_required || false,
            notes: data.notes || "",
            handover: data.handover || false,
            manager: data.manager || false,
          });
          setErrors({});
        }
      },
    }
  );

  useEffect(() => {
    if (!show) {
      setFormData({
        start_date: "",
        date_time: "",
        infection_type: "",
        cleared: false,
        hospitalized: false,
        isolation_required: false,
        notes: "",
        handover: false,
        manager: false,
      });
      setErrors({});
    }
  }, [show]);

  const updateMutation = useMutation(
    (newData) => updateInfection(selectedInfection, newData),
    {
      onSuccess: () => {
        enqueueSnackbar("Infection record updated successfully", { variant: "success" });
        queryClient.invalidateQueries([QK.infection]);
        queryClient.invalidateQueries([QK.infection, selectedInfection]);
        handleDialogClose();
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to update infection record",
          { variant: "error" }
        );
      },
    }
  );

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!formData.infection_type) {
      newErrors.infection_type = "Infection type is required";
    }
    if (!formData.start_date) {
      newErrors.start_date = "Start date is required";
    }
    if (!formData.date_time) {
      newErrors.date_time = "Time is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  }, [errors]);

  const handleCheckBox = useCallback((checked, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (validateForm()) {
      const updatedFormData = {
        ...formData,
        client: clientId,
        date_time: `${formData.start_date}T${formData.date_time}`,
      };
      updateMutation.mutate(updatedFormData);
    }
  }, [formData, clientId, updateMutation, validateForm]);

  const handleDialogClose = useCallback(() => {
    setFormData({
      start_date: "",
      date_time: "",
      infection_type: "",
      cleared: false,
      hospitalized: false,
      isolation_required: false,
      notes: "",
      handover: false,
      manager: false,
    });
    setErrors({});
    handleClose();
  }, [handleClose]);

  const isSubmitting = updateMutation.isLoading;

  if (isLoading) {
    return (
      <Dialog open={show} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={show}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="edit-infection-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
    >
      <DialogTitle id="edit-infection-dialog-title" sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Edit Infection Record
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Update infection observation details
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
            disabled={isSubmitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <form onSubmit={handleSubmit} noValidate>
        <DialogContent sx={{ p: 2.5, pt: 2 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <CustomInput
                label="Start Date *"
                type="date"
                name="start_date"
                value={formData.start_date || ""}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
                error={!!errors.start_date}
                helperText={errors.start_date}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInput
                label="Time *"
                type="time"
                name="date_time"
                value={formData.date_time || ""}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
                error={!!errors.date_time}
                helperText={errors.date_time}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <CustomSelect
                label="Infection Type *"
                name="infection_type"
                selectedValue={formData.infection_type || ""}
                onChange={handleChange}
                options={infectionTypes}
                placeholder="Select Infection Type"
                required
                error={!!errors.infection_type}
                helperText={errors.infection_type}
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: "10px",
                  bgcolor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                }}
              >
                <Stack spacing={1.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.cleared || false}
                        onChange={handleChange}
                        name="cleared"
                        color="success"
                        size="small"
                      />
                    }
                    label="Infection is now cleared"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hospitalized || false}
                        onChange={handleChange}
                        name="hospitalized"
                        color="error"
                        size="small"
                      />
                    }
                    label="Result in hospitalisation?"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isolation_required || false}
                        onChange={handleChange}
                        name="isolation_required"
                        color="warning"
                        size="small"
                      />
                    }
                    label="Isolation required"
                  />
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <CustomTextArea
                label="Clinical Notes"
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                rows={3}
                placeholder="Describe the symptoms or observation..."
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <CustomCheckbox
                  label="Share with Manager"
                  name="manager"
                  checked={formData.manager || false}
                  onClick={handleCheckBox}
                />
                <CustomCheckbox
                  label="Handover"
                  name="handover"
                  checked={formData.handover || false}
                  onClick={handleCheckBox}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            disabled={isSubmitting}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              borderColor: "#D0D5DD",
              color: "#344054",
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              bgcolor: "#2469F0",
              px: 3,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {isSubmitting ? "Saving..." : "Update Record"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default React.memo(EditInfectionModal);